import { Input } from "@/components/ui/input";
import { useCallback, useEffect, useState } from "react";
import { Download, Loader2, Clock, CheckCircle2, AlertTriangle, Landmark } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/auth/AuthContext";
import { getFoundationActualsStatus, pullFoundationActualsAll, putFoundationSetting } from "@/lib/api";

const when = (iso) => (iso ? new Date(iso).toLocaleString([], { dateStyle: "medium", timeStyle: "short" }) : "never");
const TONE = { ok: "bg-emerald-50 text-emerald-700 border-emerald-200", partial: "bg-amber-50 text-amber-800 border-amber-200",
  failed: "bg-rose-50 text-rose-700 border-rose-200", skipped: "bg-slate-100 text-slate-600 border-slate-200", running: "bg-blue-50 text-blue-700 border-blue-200" };
const FINANCE = ["admin", "accounting", "office"];

// SVC-FIX-3 — direct costs (job cost actuals) from Foundation: schedule, last run, pull now.
export default function FoundationActualsCard() {
  const { user } = useAuth();
  const canPull = FINANCE.includes((user?.role || "").toLowerCase());
  const [st, setSt] = useState(null);
  const [pulling, setPulling] = useState(false);
  const [result, setResult] = useState(null);
  const [hour, setHour] = useState(2);
  const [minute, setMinute] = useState(0);
  const [savingTime, setSavingTime] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await getFoundationActualsStatus();
      setSt(data); setHour(data.schedule.hour); setMinute(data.schedule.minute);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not load the direct-cost pull status"); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const saveTime = async () => {
    setSavingTime(true);
    try {
      await putFoundationSetting("actuals_pull_hour", Number(hour));
      const r = await putFoundationSetting("actuals_pull_minute", Number(minute));
      toast.success(`Nightly pull now runs at ${Number(hour) % 12 || 12}:${String(minute).padStart(2, "0")} ${Number(hour) < 12 ? "AM" : "PM"} MT${r.data?.next_run ? ` · next ${when(r.data.next_run)}` : ""}`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not save the time"); }
    finally { setSavingTime(false); }
  };

  const pullNow = async () => {
    setPulling(true); setResult(null);
    try {
      const { data } = await pullFoundationActualsAll();
      setResult(data);
      toast[data.status === "ok" ? "success" : data.status === "skipped" ? "warning" : "error"](data.message || "Pull finished");
      load();
    } catch (e) {
      const d = e?.response?.data?.detail || "Pull failed";
      setResult({ status: "failed", message: d });
      toast.error(d, { duration: 8000 });
    } finally { setPulling(false); }
  };

  const last = st?.last_run;
  const bridgeDown = st?.bridge && !st.bridge.ok;
  return (
    <section className="rounded-md border border-slate-200 p-3 space-y-3" data-testid="foundation-actuals-card">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="text-xs">
          <div className="font-semibold text-slate-700 uppercase tracking-wide mb-0.5 inline-flex items-center gap-1"><Landmark className="w-3.5 h-3.5" /> Direct costs from Foundation</div>
          <div className="text-slate-600" data-testid="actuals-schedule-label">
            Pulls <strong>{st?.schedule?.label || "nightly"}</strong>{st?.schedule?.next_run ? ` · next ${when(st.schedule.next_run)}` : ""} · {st?.jobs_in_scope ?? "…"} pushed job(s) in scope · read-only (Foundation is the source)
          </div>
          <div className="text-[11px] text-slate-400 mt-0.5">Sources: {(st?.sources || []).join(" · ")}. Lands in: {st?.lands_in || "WO Actuals card"}.</div>
        </div>
        <Button size="sm" className="h-8 text-xs bg-slate-800 hover:bg-slate-900 text-white" disabled={!canPull || pulling || bridgeDown} onClick={pullNow}
                title={!canPull ? "Finance / Admin only" : bridgeDown ? `Bridge offline — ${st.bridge.summary}` : "Pull every pushed job now"} data-testid="actuals-pull-now-btn">
          {pulling ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Download className="w-3 h-3 mr-1" />}
          {pulling ? "Pulling…" : "Pull direct costs now"}
        </Button>
      </div>

      {bridgeDown && (
        <div className="text-xs text-rose-700 bg-rose-50 border border-rose-200 rounded px-2 py-1.5 inline-flex items-center gap-1" data-testid="actuals-bridge-down">
          <AlertTriangle className="w-3.5 h-3.5" /> Can't pull right now — {st.bridge.summary}. The nightly pull retries automatically.
        </div>
      )}

      <div className="flex items-center gap-3 flex-wrap text-xs" data-testid="actuals-last-run">
        <Clock className="w-3.5 h-3.5 text-slate-400" />
        <span className="text-slate-600">Last run {when(last?.started_at)}</span>
        {last && <span className={`text-[10px] px-1.5 py-0.5 rounded-full border font-semibold ${TONE[last.status] || TONE.skipped}`} data-testid="actuals-last-run-status">{last.status}</span>}
        {last?.message && <span className="text-slate-500">{last.message}</span>}
        {last?.trigger && <span className="text-slate-400">· {last.trigger}</span>}
      </div>

      {result && (
        <div className={`text-xs rounded px-2 py-1.5 border ${TONE[result.status] || TONE.skipped}`} data-testid="actuals-pull-result">
          {result.status === "ok" ? <CheckCircle2 className="w-3.5 h-3.5 inline mr-1" /> : <AlertTriangle className="w-3.5 h-3.5 inline mr-1" />}
          {result.message}{result.failed?.length ? ` — ${result.failed.map(f => f.job_no).join(", ")}` : ""}
        </div>
      )}

      <div className="flex items-center gap-2 text-xs flex-wrap" data-testid="actuals-schedule-editor">
        <span className="text-slate-600">Nightly time (MT):</span>
        <Input type="number" min={0} max={23} value={hour} onChange={(e) => setHour(e.target.value)} className="h-7 w-14 px-1 text-xs" disabled={!canPull} data-testid="actuals-hour-input" />
        <span>:</span>
        <Input type="number" min={0} max={59} value={minute} onChange={(e) => setMinute(e.target.value)} className="h-7 w-14 px-1 text-xs" disabled={!canPull} data-testid="actuals-minute-input" />
        <Button size="sm" variant="outline" className="h-7 text-xs" disabled={!canPull || savingTime} onClick={saveTime} data-testid="actuals-save-time-btn">
          {savingTime ? <Loader2 className="w-3 h-3 animate-spin" /> : "Save time"}
        </Button>
      </div>

      {st?.recent_runs?.length > 1 && (
        <details className="text-xs" data-testid="actuals-run-history">
          <summary className="cursor-pointer text-slate-500">Run history ({st.recent_runs.length})</summary>
          <ul className="mt-1 space-y-0.5">
            {st.recent_runs.map(r => (
              <li key={r.id} className="text-slate-600"><span className="text-slate-400">{when(r.started_at)}</span> · <span className="font-medium">{r.status}</span> · {r.message || "—"} <span className="text-slate-400">({r.trigger})</span></li>
            ))}
          </ul>
        </details>
      )}
    </section>
  );
}
